import React, { useState, useEffect, useMemo, useCallback } from "react";

const DAY_MS = 86_400_000;

function parseDate(str) {
  if (!str) return null;
  let d = new Date(str);
  if (!isNaN(d)) return d;
  const m = str.match(/^(\d{1,2})\/([A-Za-z]{3})\/(\d{4})$/);
  if (m) return new Date(`${m[3]}-${m[2]}-${m[1]}`);
  return null;
}

function daysSince(dateStr) {
  const d = parseDate(dateStr);
  if (!d || isNaN(d)) return null;
  return Math.floor((Date.now() - d.getTime()) / DAY_MS);
}

function stalenessLabel(days) {
  if (days === null) return { label: "No data", color: "var(--danger)" };
  if (days < 30)  return { label: "Active",   color: "var(--success)" };
  if (days < 90)  return { label: "Inactive", color: "var(--warn)" };
  return           { label: "Stale",    color: "var(--danger)" };
}

function getMismatch(a) {
  const jira  = a.assignedTo?.trim();
  const def   = a.defenderLastUser?.trim();
  const hasJira = !!jira;
  const hasDef  = !!def;
  if (hasJira && hasDef && jira.toLowerCase() !== def.toLowerCase()) return "conflict";
  if (hasJira !== hasDef) return "one-sided";
  return null;
}

function isMismatch(a) { return getMismatch(a) !== null; }

function fieldIncludes(value, needle) {
  return String(value || "").toLowerCase().includes(needle);
}

function csvCell(value) {
  let text = value === null || value === undefined ? "" : String(value);
  if (/^[=+\-@]/.test(text)) text = `'${text}`;
  return `"${text.replace(/"/g, '""')}"`;
}

function downloadCsv(filename, headers, rows) {
  const csv = [headers, ...rows]
    .map(row => row.map(csvCell).join(","))
    .join("\r\n");
  const url = URL.createObjectURL(new Blob(["\uFEFF", csv], { type: "text/csv;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

function LastSeenBadge({ days }) {
  if (days === null) return <span style={{ color: "var(--muted)", fontSize: 11 }}>No data</span>;
  const { color } = stalenessLabel(days);
  return <span style={{ color, fontSize: 12, fontWeight: 500 }}>{days === 0 ? "Today" : `${days}d ago`}</span>;
}

function formatDateTime(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" });
}

function formatDuration(ms) {
  if (!Number.isFinite(Number(ms))) return "";
  const seconds = Math.round(Number(ms) / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remaining = seconds % 60;
  return remaining ? `${minutes}m ${remaining}s` : `${minutes}m`;
}

function StatusChip({ status }) {
  if (!status) return <span style={{ color: "var(--muted)", fontSize: 11 }}>—</span>;
  const color = /active|in use/i.test(status) ? "var(--success)"
    : /repair|loan/i.test(status) ? "var(--warn)"
    : "var(--muted)";
  return <span style={{ color, fontSize: 11 }}>{status}</span>;
}

function WorkflowRunChip({ status }) {
  const value = String(status || "Unknown");
  const color = /succeeded/i.test(value) ? "var(--success)"
    : /running|waiting/i.test(value) ? "var(--warn)"
    : /failed|cancelled|timedout/i.test(value) ? "var(--danger)"
    : "var(--muted)";
  return <span style={{ color, fontSize: 12, fontWeight: 800 }}>{value}</span>;
}

function hasBatteryThreshold(value, threshold, comparison) {
  if (value === null || value === undefined || value === "") return false;
  const number = Number(value);
  return Number.isFinite(number) && comparison(number, threshold);
}

function isBatteryRecordFlagged(record) {
  const inspectionRequired = String(record?.inspectionRequired || "").toLowerCase();
  return record?.flagged === true
    || inspectionRequired === "true"
    || inspectionRequired === "1"
    || inspectionRequired === "yes"
    || hasBatteryThreshold(record?.batteryHealthPercent, 80, (value, threshold) => value <= threshold)
    || hasBatteryThreshold(record?.cycleCount, 500, (value, threshold) => value >= threshold)
    || /flagged/i.test(record?.layer1Status || "");
}

function HealthChip({ value }) {
  const n = Number(value);
  if (!Number.isFinite(n)) return <span style={{ color: "var(--muted)", fontSize: 11 }}>—</span>;
  const color = n <= 80 ? "var(--danger)" : n <= 90 ? "var(--warn)" : "var(--success)";
  return <span style={{ color, fontSize: 12, fontWeight: 700 }}>{Math.round(n)}%</span>;
}

function TicketChip({ ticket }) {
  if (!ticket?.key) return <span style={{ color: "var(--danger)", fontSize: 11, fontWeight: 700 }}>No open ticket</span>;
  return (
    <a href={ticket.url} target="_blank" rel="noreferrer" style={{ color: "var(--accent)", fontSize: 12, fontWeight: 700, textDecoration: "none", whiteSpace: "nowrap" }}>
      {ticket.key}
    </a>
  );
}

function FilterGroup({ label, options, value, onChange, labelMap = {} }) {
  return (
    <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
      <span style={{ fontSize: 11, color: "var(--muted)", marginRight: 2, whiteSpace: "nowrap" }}>{label}:</span>
      {options.map(o => (
        <button key={o} onClick={() => onChange(o)} style={{
          padding: "4px 10px", borderRadius: 6, cursor: "pointer", fontSize: 12,
          fontWeight: value === o ? 600 : 400, whiteSpace: "nowrap",
          border: `1px solid ${value === o ? "var(--accent)" : "var(--border)"}`,
          background: value === o ? "rgba(79,156,249,0.12)" : "transparent",
          color: value === o ? "var(--accent)" : "var(--muted)",
        }}>
          {labelMap[o] ?? o}
        </button>
      ))}
    </div>
  );
}

const TH = ({ col, label, sortCol, sortDir, onSort }) => (
  <th onClick={() => onSort(col)} style={{ padding: "10px 14px", fontSize: 10, color: sortCol === col ? "var(--accent)" : "var(--muted)", textTransform: "uppercase", letterSpacing: "0.07em", fontWeight: 600, background: "var(--bg3)", borderBottom: "1px solid var(--border)", textAlign: "left", whiteSpace: "nowrap", cursor: "pointer", userSelect: "none" }}>
    {label}{sortCol === col ? (sortDir === "asc" ? " ↑" : " ↓") : ""}
  </th>
);

const DATE_OPTIONS = ["All", "Last 1yr", "Last 2yr", "Last 3yr", "Last 3.5yr"];
const DATE_YEARS   = { "Last 1yr": 1, "Last 2yr": 2, "Last 3yr": 3, "Last 3.5yr": 3.5 };
const TABS = [
  { id: "active",      label: "Active" },
  { id: "stale-jira",  label: "Stale (Jira)" },
  { id: "stale-azure", label: "Stale (Azure)" },
  { id: "battery",     label: "Battery Health" },
];

const BATTERY_SESSION = {
  devices: [],
  summary: null,
  workflowRuns: [],
  workflowLoadedAt: null,
  workflowError: null,
  loadedAt: null,
  error: null,
  requested: false,
  mode: "flagged",
};

export default function Inventory({
  defenderMap = {},
  inventoryDevices = [],
  inventoryLoading = false,
  inventoryLoadedAt = null,
  inventoryError = null,
  loadInventory,
  inventoryDefenderLoading = false,
  inventoryDefenderProgress = 0,
  mdeSweepLoading = false,
  mdeSweepProgress = 0,
  mdeSweepTotal = 0,
  mdeSweepFound = 0,
  mdeSweepDone = false,
}) {
  const [activeTab,      setActiveTab]      = useState("active");
  const [typeFilter,     setTypeFilter]     = useState("All");
  const [locationFilter, setLocationFilter] = useState("All");
  const [dateFilter,     setDateFilter]     = useState("All");
  const [search,         setSearch]         = useState("");
  const [mismatchOnly,   setMismatchOnly]   = useState(false);
  const [staleOnly,      setStaleOnly]      = useState(false);
  const [sortCol,        setSortCol]        = useState("serial");
  const [sortDir,        setSortDir]        = useState("asc");
  const [batteryDevices, setBatteryDevices] = useState(BATTERY_SESSION.devices);
  const [batteryMode, setBatteryMode] = useState(BATTERY_SESSION.mode || "flagged");
  const [batterySummary, setBatterySummary] = useState(BATTERY_SESSION.summary);
  const [batteryLoading, setBatteryLoading] = useState(false);
  const [batteryLoadedAt, setBatteryLoadedAt] = useState(BATTERY_SESSION.loadedAt);
  const [batteryError, setBatteryError] = useState(BATTERY_SESSION.error);
  const [batteryRequested, setBatteryRequested] = useState(BATTERY_SESSION.requested);
  const [batteryWorkflowRuns, setBatteryWorkflowRuns] = useState(BATTERY_SESSION.workflowRuns);
  const [batteryWorkflowLoadedAt, setBatteryWorkflowLoadedAt] = useState(BATTERY_SESSION.workflowLoadedAt);
  const [batteryWorkflowError, setBatteryWorkflowError] = useState(BATTERY_SESSION.workflowError);
  const [batteryWorkflowLoading, setBatteryWorkflowLoading] = useState(false);
  const [jiraImportAutomation, setJiraImportAutomation] = useState(null);
  const [jiraImportAutomationError, setJiraImportAutomationError] = useState("");
  const [jiraImportAutomationLoading, setJiraImportAutomationLoading] = useState(false);

  const handleSort = col => {
    setSortDir(d => sortCol === col && d === "asc" ? "desc" : "asc");
    setSortCol(col);
  };

  useEffect(() => {
    if (inventoryDevices.length === 0 && !inventoryLoading && !inventoryLoadedAt && loadInventory) {
      loadInventory();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;
    setJiraImportAutomationLoading(true);
    fetch("/api/jira-import-lwt", { cache: "no-store" })
      .then(async res => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
        if (!cancelled) {
          setJiraImportAutomation(data.status || null);
          setJiraImportAutomationError("");
        }
      })
      .catch(e => {
        if (!cancelled) setJiraImportAutomationError(String(e.message || e));
      })
      .finally(() => {
        if (!cancelled) setJiraImportAutomationLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const loadBatteryWorkflowRuns = async () => {
    setBatteryWorkflowLoading(true);
    setBatteryWorkflowError(null);
    BATTERY_SESSION.workflowError = null;
    try {
      const res = await fetch("/api/battery-workflow-runs", { cache: "no-store" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      const runs = data.runs || [];
      const loadedAt = data.loadedAt || new Date().toISOString();
      BATTERY_SESSION.workflowRuns = runs;
      BATTERY_SESSION.workflowLoadedAt = loadedAt;
      setBatteryWorkflowRuns(runs);
      setBatteryWorkflowLoadedAt(loadedAt);
    } catch (e) {
      const message = String(e.message || e);
      BATTERY_SESSION.workflowError = message;
      setBatteryWorkflowError(message);
    } finally {
      setBatteryWorkflowLoading(false);
    }
  };

  const loadBatteryHealth = async () => {
    BATTERY_SESSION.requested = true;
    setBatteryRequested(true);
    setBatteryLoading(true);
    setBatteryError(null);
    BATTERY_SESSION.error = null;
    loadBatteryWorkflowRuns();
    try {
      const res = await fetch("/api/battery-health?mode=all", { cache: "no-store" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      const devices = data.devices || [];
      const summary = data.summary || null;
      const loadedAt = data.loadedAt || new Date().toISOString();
      BATTERY_SESSION.devices = devices;
      BATTERY_SESSION.summary = summary;
      BATTERY_SESSION.mode = batteryMode;
      BATTERY_SESSION.loadedAt = loadedAt;
      setBatteryDevices(devices);
      setBatterySummary(summary);
      setBatteryLoadedAt(loadedAt);
    } catch (e) {
      const message = String(e.message || e);
      BATTERY_SESSION.error = message;
      setBatteryError(message);
    } finally {
      setBatteryLoading(false);
    }
  };

  useEffect(() => {
    if (!batteryRequested && !batteryLoading) {
      loadBatteryHealth();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batteryRequested, batteryLoading]);

  const loadedCount = inventoryDevices.length;
  const done        = inventoryLoadedAt !== null;

  const handleBatteryModeChange = mode => {
    if (mode === batteryMode) return;
    BATTERY_SESSION.mode = mode;
    setBatteryMode(mode);
  };

  const enriched = useMemo(() => inventoryDevices.map(a => {
    const d = defenderMap[a.serial?.toUpperCase()];
    const defenderLastSeen = d?.lastLogonTime || d?.lastSeen || null;
    return {
      ...a,
      defenderLastLogon: defenderLastSeen,
      defenderLastUser:  d?.lastLogonUser || null,
      defenderDeviceName: d?.deviceName || null,
      intuneHostname: d?.deviceName || a.hostname || null,
    };
  }), [inventoryDevices, defenderMap]);

  const inventoryBySerial = useMemo(() => {
    const map = new Map();
    enriched.forEach(item => {
      const key = String(item.serial || "").toUpperCase();
      if (key) map.set(key, item);
    });
    return map;
  }, [enriched]);

  // Five-bucket summary stats
  const stats = useMemo(() => ({
    total:      enriched.length,
    withAzure:  enriched.filter(a => a.defenderLastLogon !== null).length,
    fresh:      enriched.filter(a => { const n = daysSince(a.defenderLastLogon); return n !== null && n < 30; }).length,
    dormant:    enriched.filter(a => { const n = daysSince(a.defenderLastLogon); return n !== null && n >= 30 && n < 90; }).length,
    staleAzure: enriched.filter(a => { const n = daysSince(a.defenderLastLogon); return n !== null && n >= 90; }).length,
    staleJira:  enriched.filter(a => a.defenderLastLogon === null).length,
    mismatches: enriched.filter(isMismatch).length,
  }), [enriched]);

  const types     = useMemo(() => ["All", ...Array.from(new Set(inventoryDevices.map(a => a.type).filter(Boolean))).sort()],     [inventoryDevices]);
  const locations = useMemo(() => ["All", ...Array.from(new Set(inventoryDevices.map(a => a.location).filter(Boolean))).sort()], [inventoryDevices]);
  const normalizedSearch = search.trim().toLowerCase();
  const matchesSearch = useCallback(a => {
    if (!normalizedSearch) return true;
    return [
      a.serial,
      a.assignedTo,
      a.defenderLastUser,
      a.userPrincipalName,
      a.description,
      a.deviceName,
      a.defenderDeviceName,
      a.intuneHostname,
      a.eolYear,
      a.jiraKey,
      a.jiraAssetKey,
      a.ticket?.assignee,
      a.ticket?.assigneeEmail,
    ].some(value => fieldIncludes(value, normalizedSearch));
  }, [normalizedSearch]);

  // Base filter — applied across all tabs
  const baseFiltered = useMemo(() => enriched.filter(a => {
    if (!matchesSearch(a)) return false;
    if (typeFilter !== "All" && a.type !== typeFilter) return false;
    if (locationFilter !== "All" && a.location !== locationFilter) return false;
    if (dateFilter !== "All") {
      const years = DATE_YEARS[dateFilter];
      const d = parseDate(a.dateOrdered);
      if (!d || d.getTime() < Date.now() - years * 365.25 * DAY_MS) return false;
    }
    return true;
  }), [enriched, matchesSearch, typeFilter, locationFilter, dateFilter]);

  const mismatchCount = useMemo(() => baseFiltered.filter(isMismatch).length, [baseFiltered]);

  // Tab-specific device sets
  const activeDevices = useMemo(() => {
    let list = baseFiltered.filter(a => a.defenderLastLogon !== null);
    if (staleOnly)     list = list.filter(a => (daysSince(a.defenderLastLogon) ?? Infinity) >= 90);
    if (mismatchOnly)  list = list.filter(isMismatch);
    return list.sort((a, b) => {
      const av = String(a[sortCol] ?? ""), bv = String(b[sortCol] ?? "");
      return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
    });
  }, [baseFiltered, staleOnly, mismatchOnly, sortCol, sortDir]);

  const staleJiraDevices = useMemo(() => {
    let list = baseFiltered.filter(a => a.defenderLastLogon === null);
    if (mismatchOnly) list = list.filter(isMismatch);
    return list.sort((a, b) => {
      const av = String(a[sortCol] ?? ""), bv = String(b[sortCol] ?? "");
      return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
    });
  }, [baseFiltered, mismatchOnly, sortCol, sortDir]);

  const staleAzureDevices = useMemo(() => {
    let list = baseFiltered.filter(a => {
      const n = daysSince(a.defenderLastLogon);
      return n !== null && n >= 90;
    });
    if (mismatchOnly) list = list.filter(isMismatch);
    // Default sort: oldest last-seen first
    return list.sort((a, b) => {
      if (sortCol !== "serial") {
        const av = String(a[sortCol] ?? ""), bv = String(b[sortCol] ?? "");
        return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      return (daysSince(b.defenderLastLogon) ?? 0) - (daysSince(a.defenderLastLogon) ?? 0);
    });
  }, [baseFiltered, mismatchOnly, sortCol, sortDir]);

  const batteryViewDevices = useMemo(
    () => batteryMode === "flagged"
      ? batteryDevices.filter(isBatteryRecordFlagged)
      : batteryDevices,
    [batteryDevices, batteryMode]
  );

  const batteryViewSummary = useMemo(() => {
    if (!batterySummary) return null;
    return {
      total: batteryViewDevices.length,
      flagged: batteryViewDevices.filter(isBatteryRecordFlagged).length,
      normal: batteryViewDevices.filter(a => !isBatteryRecordFlagged(a)).length,
      under80: batteryViewDevices.filter(a => hasBatteryThreshold(a.batteryHealthPercent, 80, (value, threshold) => value <= threshold)).length,
      over500: batteryViewDevices.filter(a => hasBatteryThreshold(a.cycleCount, 500, (value, threshold) => value >= threshold)).length,
      openTickets: batteryViewDevices.filter(a => a.ticket?.key).length,
      missingTickets: batteryViewDevices.filter(a => isBatteryRecordFlagged(a) && !a.ticket?.key).length,
    };
  }, [batteryViewDevices, batterySummary]);

  const batteryRows = useMemo(() => {
    const withAzure = batteryViewDevices.map(a => {
      const serialKey = String(a.serial || "").toUpperCase();
      const d = defenderMap[serialKey];
      const inv = inventoryBySerial.get(serialKey) || {};
      const defenderLastSeen = d?.lastLogonTime || d?.lastSeen || inv.defenderLastLogon || null;
      return {
        ...inv,
        ...a,
        campus: a.campus || inv.location || inv.campus || null,
        location: a.campus || inv.location || null,
        assignedTo: a.assignedTo || inv.assignedTo || null,
        jiraDescription: a.jiraDescription || inv.description || null,
        description: a.jiraDescription || inv.description || null,
        dateOrdered: a.dateOrdered || inv.dateOrdered || null,
        eolYear: a.eolYear || inv.eolYear || null,
        jiraStatus: a.jiraStatus || inv.status || null,
        intuneHostname: d?.deviceName || inv.intuneHostname || inv.hostname || a.deviceName || null,
        defenderLastLogon: defenderLastSeen,
        defenderLastUser: d?.lastLogonUser || inv.defenderLastUser || null,
      };
    });

    const filtered = normalizedSearch
      ? withAzure.filter(matchesSearch)
      : withAzure;

    return filtered.sort((a, b) => {
      if (["batteryHealthPercent", "cycleCount"].includes(sortCol)) {
        const av = Number(a[sortCol]) || 0;
        const bv = Number(b[sortCol]) || 0;
        return sortDir === "asc" ? av - bv : bv - av;
      }
      if (sortCol === "captureDate") {
        const av = Date.parse(a.captureDate || "") || 0;
        const bv = Date.parse(b.captureDate || "") || 0;
        return sortDir === "asc" ? av - bv : bv - av;
      }
      if (sortCol === "defenderLastLogon") {
        const av = Date.parse(a.defenderLastLogon || "") || 0;
        const bv = Date.parse(b.defenderLastLogon || "") || 0;
        return sortDir === "asc" ? av - bv : bv - av;
      }
      if (sortCol === "eolYear") {
        const av = Number(a.eolYear) || 0;
        const bv = Number(b.eolYear) || 0;
        return sortDir === "asc" ? av - bv : bv - av;
      }
      if (sortCol === "ticket") {
        const av = a.ticket?.key || "";
        const bv = b.ticket?.key || "";
        return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      if (sortCol === "ticketStatus") {
        const av = a.ticket?.status || "";
        const bv = b.ticket?.status || "";
        return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      if (sortCol === "ticketAssignee") {
        const av = a.ticket?.assignee || a.ticket?.assigneeEmail || "";
        const bv = b.ticket?.assignee || b.ticket?.assigneeEmail || "";
        return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      const av = String(a[sortCol] ?? "");
      const bv = String(b[sortCol] ?? "");
      return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
    });
  }, [batteryViewDevices, defenderMap, inventoryBySerial, normalizedSearch, matchesSearch, sortCol, sortDir]);

  const tabDevices = activeTab === "active" ? activeDevices
    : activeTab === "stale-jira"  ? staleJiraDevices
    : activeTab === "stale-azure" ? staleAzureDevices
    : batteryRows;

  const exportCurrentView = () => {
    const tabLabel = TABS.find(tab => tab.id === activeTab)?.label || "Inventory";
    const viewLabel = activeTab === "battery"
      ? `${tabLabel} - ${batteryMode === "flagged" ? "Flagged" : "All Recent"}`
      : tabLabel;
    if (tabDevices.length === 0) return;
    if (!window.confirm(`Export ${tabDevices.length} device${tabDevices.length === 1 ? "" : "s"} from the current ${viewLabel} view? The CSV will use the search, filters and sorting currently applied.`)) return;

    let headers;
    let rows;
    if (activeTab === "stale-jira") {
      headers = ["Serial", "Description", "Type", "Status", "Location", "Assigned (Jira)", "Date Ordered"];
      rows = tabDevices.map(a => [a.serial, a.description, a.type, a.status, a.location, a.assignedTo, a.dateOrdered]);
    } else if (activeTab === "battery") {
      headers = ["Serial", "Device", "Campus", "Jira Assigned To", "EOL", "Last Login (Azure)", "Last Seen", "Battery Health Percent", "Cycle Count", "Reason", "Captured", "Ticket", "Ticket Assigned To", "Ticket Status"];
      rows = tabDevices.map(a => [
        a.serial,
        a.deviceName,
        a.campus,
        a.assignedTo,
        a.eolYear,
        a.defenderLastUser,
        formatDateTime(a.defenderLastLogon),
        a.batteryHealthPercent,
        a.cycleCount,
        a.notes || a.layer1Status,
        formatDateTime(a.captureDate),
        a.flagged ? (a.ticket?.key || "No open ticket") : "Not flagged",
        a.flagged ? (a.ticket?.assignee || a.ticket?.assigneeEmail || "Unassigned") : "",
        a.flagged ? (a.ticket?.status || a.ticketLookupError || "") : "",
      ]);
    } else {
      headers = ["Serial", "Intune Hostname", "Description", "Type", "Status", "Location", "Assigned (Jira)", "Last Login (Azure)", "Last Seen", "Activity", "Date Ordered"];
      rows = tabDevices.map(a => {
        const days = daysSince(a.defenderLastLogon);
        return [a.serial, a.intuneHostname, a.description, a.type, a.status, a.location, a.assignedTo, a.defenderLastUser, formatDateTime(a.defenderLastLogon), stalenessLabel(days).label, a.dateOrdered];
      });
    }

    const date = new Date().toISOString().slice(0, 10);
    const mode = activeTab === "battery" ? `-${batteryMode}` : "";
    downloadCsv(`inventory-${activeTab}${mode}-${date}.csv`, headers, rows);
  };

  return (
    <div className="fade-up">
      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 26, fontWeight: 800, letterSpacing: "-0.03em", color: "var(--text)", marginBottom: 4 }}>Inventory</h1>
          <div style={{ fontSize: 13, color: "var(--muted)" }}>
            Starts with Jira as the source of truth, then cross-checks each device against Defender/Intune activity.
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          {loadedCount > 0 && (
            <button onClick={exportCurrentView} disabled={tabDevices.length === 0} style={{ display: "flex", alignItems: "center", gap: 6, background: "var(--accent)", border: "1px solid var(--accent)", color: "#fff", padding: "7px 14px", borderRadius: 8, cursor: tabDevices.length === 0 ? "default" : "pointer", fontSize: 12, fontWeight: 700, opacity: tabDevices.length === 0 ? 0.5 : 1 }}>
              Export CSV
            </button>
          )}
        {loadInventory && (
          <button onClick={loadInventory} disabled={inventoryLoading} style={{ display: "flex", alignItems: "center", gap: 6, background: "var(--bg2)", border: "1px solid var(--border)", color: "var(--muted)", padding: "7px 14px", borderRadius: 8, cursor: inventoryLoading ? "default" : "pointer", fontSize: 12, opacity: inventoryLoading ? 0.6 : 1 }}>
            {inventoryLoading ? <><span className="spinner" style={{ width: 10, height: 10 }} /> Loading…</> : "↺ Reload Inventory"}
          </button>
        )}
        </div>
      </div>

      {/* Phase 1 — Jira fetch */}
      {inventoryLoading && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
            <span className="spinner" style={{ width: 14, height: 14, flexShrink: 0 }} />
            <span style={{ fontSize: 13, color: "var(--muted)" }}>
              Loading inventory…{loadedCount > 0 ? ` ${loadedCount} / ~1090 devices (Jira)` : ""}
            </span>
          </div>
          <div style={{ height: 3, background: "var(--border)", borderRadius: 2, overflow: "hidden" }}>
            <div style={{ height: "100%", background: "var(--accent)", borderRadius: 2, width: `${Math.min(100, (loadedCount / 1090) * 100)}%`, transition: "width 0.3s ease" }} />
          </div>
        </div>
      )}

      {/* Phase 2 — Defender fetch */}
      {!inventoryLoading && inventoryDefenderLoading && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
            <span className="spinner" style={{ width: 14, height: 14, flexShrink: 0 }} />
            <span style={{ fontSize: 13, color: "var(--muted)" }}>
              Fetching last login data…{inventoryDefenderProgress > 0 ? ` ${Math.min(inventoryDefenderProgress, loadedCount)} / ${loadedCount} (Azure)` : " (Azure)"}
            </span>
          </div>
          <div style={{ height: 3, background: "var(--border)", borderRadius: 2, overflow: "hidden" }}>
            <div style={{ height: "100%", background: "#a78bfa", borderRadius: 2, width: `${loadedCount > 0 ? Math.min(100, (inventoryDefenderProgress / loadedCount) * 100) : 0}%`, transition: "width 0.3s ease" }} />
          </div>
        </div>
      )}

      {/* Phase 3 — MDE sweep */}
      {!inventoryLoading && !inventoryDefenderLoading && mdeSweepLoading && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
            <span className="spinner" style={{ width: 14, height: 14, flexShrink: 0, borderTopColor: "#a78bfa" }} />
            <span style={{ fontSize: 13, color: "var(--muted)" }}>
              MDE sweep: {mdeSweepProgress} / {mdeSweepTotal} stale devices checked{mdeSweepFound > 0 ? ` · ${mdeSweepFound} found` : ""}
            </span>
          </div>
          <div style={{ height: 3, background: "var(--border)", borderRadius: 2, overflow: "hidden" }}>
            <div style={{ height: "100%", background: "#7c3aed", borderRadius: 2, width: `${mdeSweepTotal > 0 ? Math.min(100, (mdeSweepProgress / mdeSweepTotal) * 100) : 0}%`, transition: "width 0.3s ease" }} />
          </div>
        </div>
      )}

      {!inventoryLoading && !inventoryDefenderLoading && !mdeSweepLoading && mdeSweepDone && mdeSweepTotal > 0 && (
        <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 12 }}>
          MDE sweep complete — {mdeSweepFound > 0 ? `${mdeSweepFound} additional device${mdeSweepFound !== 1 ? "s" : ""} found` : "no additional devices found"} ({mdeSweepTotal} checked)
        </div>
      )}

      {done && !inventoryDefenderLoading && !mdeSweepLoading && loadedCount > 0 && (
        <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 20 }}>
          {stats.total} devices · {stats.withAzure} with Azure data · last loaded {new Date(inventoryLoadedAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit", hour12: true })}
        </div>
      )}

      {loadedCount > 0 && (
        <div style={{ background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 12, padding: "14px 18px", marginBottom: 20 }}>
          <div style={{ fontSize: 11, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8, fontWeight: 700 }}>Sync Rules</div>
          <div style={{ display: "grid", gap: 6, fontSize: 12, color: "var(--muted)", lineHeight: 1.65 }}>
            <div><strong style={{ color: "var(--text)" }}>Jira Inventory</strong> is the starting source of truth for this page.</div>
            <div><strong style={{ color: "var(--text)" }}>Recent scan results</strong> are reused first to keep reloads fast. Most Azure matches are reused for 1 day. Devices found later through deeper stale-device checks are also reused for 1 day. Devices not found are skipped for 1 day before being checked again.</div>
            <div><strong style={{ color: "var(--text)" }}>Azure last login data</strong> is then refreshed for any remaining devices during each inventory reload.</div>
            <div><strong style={{ color: "var(--text)" }}>Extra stale-device checks</strong> run last for devices not found in the Azure scan, to improve coverage before a device is treated as stale.</div>
          </div>
        </div>
      )}

      {loadedCount > 0 && (
        <div style={{ background: "rgba(79,156,249,0.06)", border: "1px solid rgba(79,156,249,0.2)", borderRadius: 12, padding: "12px 16px", marginBottom: 20, color: "var(--muted)", fontSize: 12, lineHeight: 1.65 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "flex-start" }}>
            <div>
              <div style={{ color: "var(--text)", fontWeight: 800, fontSize: 13 }}>LWT → Jira auto-sync status</div>
              <div style={{ marginTop: 2 }}>Confirms the background process that syncs new LWT order devices straight into Jira Assets.</div>
            </div>
            {jiraImportAutomationLoading && (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 8, color: "var(--muted)", fontSize: 12 }}>
                <span className="spinner" style={{ width: 12, height: 12, flexShrink: 0 }} />
                Checking…
              </span>
            )}
          </div>
          {jiraImportAutomationError ? (
            <div style={{ marginTop: 8, color: "var(--warn)" }}>Could not load LWT → Jira sync status: {jiraImportAutomationError}</div>
          ) : jiraImportAutomation?.finishedAt ? (
            <div style={{ marginTop: 8 }}>
              Last sync: <strong style={{ color: "var(--text)" }}>{formatDateTime(jiraImportAutomation.finishedAt)}</strong>
              {" "}· Created <strong style={{ color: "var(--text)" }}>{jiraImportAutomation.result?.created ?? 0}</strong>
              {" "}· Failed <strong style={{ color: "var(--text)" }}>{jiraImportAutomation.result?.failed ?? 0}</strong>
              {" "}· Tickets <strong style={{ color: "var(--text)" }}>{jiraImportAutomation.tickets?.length ?? 0}</strong>
              {jiraImportAutomation.ticketFailures?.length ? (
                <span style={{ color: "var(--warn)" }}> · Ticket failures {jiraImportAutomation.ticketFailures.length}</span>
              ) : null}
            </div>
          ) : !jiraImportAutomationLoading ? (
            <div style={{ marginTop: 8 }}>No automated LWT → Jira sync has been recorded yet.</div>
          ) : null}
          {jiraImportAutomation?.tickets?.length > 0 && (
            <div style={{ marginTop: 6, display: "flex", gap: 8, flexWrap: "wrap" }}>
              {jiraImportAutomation.tickets.map(ticket => (
                <a key={ticket.key || ticket.campus} href={ticket.url} target="_blank" rel="noreferrer" style={{ color: "var(--accent)", textDecoration: "none", fontWeight: 700 }}>
                  {ticket.campus}: {ticket.key || "ticket"} ({ticket.count})
                </a>
              ))}
            </div>
          )}
        </div>
      )}

      {inventoryError && (
        <div style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 10, padding: "14px 18px", color: "var(--danger)", fontSize: 13, marginBottom: 20 }}>
          Failed to load inventory: {inventoryError}
        </div>
      )}

      {/* Summary stats — 5 buckets */}
      {loadedCount > 0 && (
        <div style={{ display: "flex", gap: 10, marginBottom: 24, flexWrap: "wrap" }}>
          {[
            { label: "Active <30d",       value: stats.fresh,      color: "var(--success)", sub: "seen recently" },
            { label: "Dormant 30–90d",    value: stats.dormant,    color: "var(--warn)",    sub: "slowing down" },
            { label: "Stale Azure 90+d",  value: stats.staleAzure, color: "var(--danger)",  sub: "Azure but idle" },
            { label: "Stale Jira only",   value: stats.staleJira,  color: "rgba(239,68,68,0.6)", sub: "no Azure data" },
            { label: "Assignment Conflicts (Mismatches)", value: stats.mismatches, color: "var(--warn)", sub: "Jira ≠ Defender" },
          ].map(s => (
            <div key={s.label} style={{ background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 10, padding: "12px 18px", minWidth: 120 }}>
              <div style={{ fontSize: 24, fontWeight: 700, color: s.color, fontFamily: "'DM Sans', sans-serif", lineHeight: 1 }}>{s.value}</div>
              <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 3, textTransform: "uppercase", letterSpacing: "0.07em" }}>{s.label}</div>
              <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 1 }}>{s.sub}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters — apply across all tabs */}
      {loadedCount > 0 && (
        <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
          <div style={{ flex: "1 1 260px", minWidth: 220, maxWidth: 420 }}>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search serial, Jira assignee, Azure login..."
              style={{ width: "100%", background: "var(--bg2)", border: "1px solid var(--border)", color: "var(--text)", borderRadius: 8, padding: "7px 10px", fontSize: 12, outline: "none" }}
            />
          </div>
          <FilterGroup label="Type"     options={types}        value={typeFilter}     onChange={setTypeFilter} />
          <FilterGroup label="Location" options={locations}    value={locationFilter} onChange={setLocationFilter} labelMap={{ HCC: "CCK", WCC: "CCW" }} />
          <FilterGroup label="Ordered"  options={DATE_OPTIONS} value={dateFilter}     onChange={setDateFilter} />
          <button onClick={() => setMismatchOnly(v => !v)} style={{
            padding: "4px 10px", borderRadius: 6, cursor: "pointer", fontSize: 12,
            fontWeight: mismatchOnly ? 600 : 400, whiteSpace: "nowrap",
            border: `1px solid ${mismatchOnly ? "var(--warn)" : "var(--border)"}`,
            background: mismatchOnly ? "rgba(245,158,11,0.12)" : "transparent",
            color: mismatchOnly ? "var(--warn)" : "var(--muted)",
          }}>
            Mismatches ({mismatchCount})
          </button>
        </div>
      )}

      {/* Tab bar */}
      {loadedCount > 0 && (
        <div style={{ display: "flex", gap: 2, marginBottom: 20, borderBottom: "1px solid var(--border)", paddingBottom: 0 }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)} style={{
              padding: "8px 16px", background: "none", border: "none", cursor: "pointer",
              fontSize: 13, fontWeight: activeTab === t.id ? 600 : 400,
              color: activeTab === t.id ? "var(--accent)" : "var(--muted)",
              borderBottom: `2px solid ${activeTab === t.id ? "var(--accent)" : "transparent"}`,
              marginBottom: -1, transition: "all 0.15s",
            }}>
              {t.label}
              <span style={{ marginLeft: 6, fontSize: 11, opacity: 0.7 }}>
                ({t.id === "active" ? activeDevices.length : t.id === "stale-jira" ? staleJiraDevices.length : t.id === "stale-azure" ? staleAzureDevices.length : batteryRows.length})
              </span>
            </button>
          ))}
          <span style={{ marginLeft: "auto", fontSize: 12, color: "var(--muted)", alignSelf: "center", paddingRight: 4 }}>{tabDevices.length} shown</span>
        </div>
      )}

      {/* ── Active tab ── */}
      {loadedCount > 0 && activeTab === "active" && (
        <>
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 14 }}>
            <button onClick={() => setStaleOnly(v => !v)} style={{
              padding: "4px 10px", borderRadius: 6, cursor: "pointer", fontSize: 12,
              fontWeight: staleOnly ? 600 : 400, whiteSpace: "nowrap",
              border: `1px solid ${staleOnly ? "var(--danger)" : "var(--border)"}`,
              background: staleOnly ? "rgba(239,68,68,0.1)" : "transparent",
              color: staleOnly ? "var(--danger)" : "var(--muted)",
            }}>
              Show stale only (90+d)
            </button>
            <span style={{ fontSize: 11, color: "var(--muted)" }}>
              Devices with Azure data but no recent activity — possible dormant or lost devices
            </span>
          </div>
          <div style={{ background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 12, overflow: "hidden" }}>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    {[
                      { col: "serial",            label: "Serial" },
                      { col: "intuneHostname",    label: "Intune Hostname" },
                      { col: "description",       label: "Description" },
                      { col: "type",              label: "Type" },
                      { col: "status",            label: "Status" },
                      { col: "location",          label: "Location" },
                      { col: "assignedTo",        label: "Assigned (Jira)" },
                      { col: "defenderLastUser",  label: "Last Login (Azure)" },
                      { col: "defenderLastLogon", label: "Last Seen" },
                      { col: "staleness",         label: "Activity" },
                      { col: "dateOrdered",       label: "Date Ordered" },
                    ].map(({ col, label }) => (
                      <TH key={col} col={col} label={label} sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {activeDevices.map((a, i) => {
                    const days     = daysSince(a.defenderLastLogon);
                    const mismatch = getMismatch(a);
                    const { label: sLabel, color: sColor } = stalenessLabel(days);
                    const rowBg = mismatch === "conflict"  ? "rgba(245,158,11,0.06)"
                                : mismatch === "one-sided" ? "rgba(239,68,68,0.06)"
                                : i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.01)";
                    const flagColor = mismatch === "conflict"  ? "var(--warn)"
                                    : mismatch === "one-sided" ? "var(--danger)" : null;
                    return (
                      <tr key={a.jiraKey || i} style={{ borderBottom: "1px solid var(--border)", background: rowBg }}>
                        <td style={{ padding: "8px 14px", fontSize: 12, fontFamily: "var(--font-mono)", fontWeight: 600, color: "var(--accent)", whiteSpace: "nowrap" }}>{a.serial || "—"}</td>
                        <td style={{ padding: "8px 14px", fontSize: 12, fontFamily: "var(--font-mono)", color: a.intuneHostname ? "var(--text)" : "var(--muted)", whiteSpace: "nowrap" }}>{a.intuneHostname || "—"}</td>
                        <td style={{ padding: "8px 14px", fontSize: 12, color: "var(--text)", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={a.description}>{a.description || "—"}</td>
                        <td style={{ padding: "8px 14px", fontSize: 11, color: "var(--muted)", whiteSpace: "nowrap" }}>{a.type || "—"}</td>
                        <td style={{ padding: "8px 14px", whiteSpace: "nowrap" }}><StatusChip status={a.status} /></td>
                        <td style={{ padding: "8px 14px", fontSize: 11, color: "var(--muted)", whiteSpace: "nowrap" }}>{a.location || "—"}</td>
                        <td style={{ padding: "8px 14px", fontSize: 12, color: flagColor || "var(--text)", whiteSpace: "nowrap" }}>{a.assignedTo || <span style={{ color: "var(--muted)" }}>—</span>}</td>
                        <td style={{ padding: "8px 14px", fontSize: 12, color: flagColor || "var(--muted)", whiteSpace: "nowrap" }}>{a.defenderLastUser || <span style={{ color: "var(--muted)" }}>—</span>}</td>
                        <td style={{ padding: "8px 14px", whiteSpace: "nowrap" }}><LastSeenBadge days={days} /></td>
                        <td style={{ padding: "8px 14px", whiteSpace: "nowrap" }}>
                          <span style={{ fontSize: 11, fontWeight: 600, color: sColor, background: `${sColor}18`, padding: "2px 8px", borderRadius: 5 }}>{sLabel}</span>
                        </td>
                        <td style={{ padding: "8px 14px", fontSize: 12, fontFamily: "var(--font-mono)", color: "var(--muted)", whiteSpace: "nowrap" }}>{a.dateOrdered || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {activeDevices.length === 0 && done && (
                <div style={{ padding: 40, textAlign: "center", color: "var(--muted)", fontSize: 13 }}>No devices match the current filters.</div>
              )}
            </div>
          </div>
        </>
      )}

      {/* ── Stale (Jira) tab ── */}
      {loadedCount > 0 && activeTab === "stale-jira" && (
        <>
          <div style={{ background: "rgba(239,68,68,0.07)", border: "1px solid rgba(239,68,68,0.2)", borderRadius: 10, padding: "10px 16px", fontSize: 13, color: "var(--muted)", marginBottom: 16 }}>
            These <strong style={{ color: "var(--text)" }}>{stats.staleJira} devices</strong> exist in Jira Assets but have <strong style={{ color: "var(--text)" }}>no Microsoft Intune / Azure activity</strong>. This means they are either <strong style={{ color: "var(--text)" }}>genuinely decommissioned</strong> (removed from service but not retired in Jira), or <strong style={{ color: "var(--text)" }}>never enrolled in Intune</strong> (e.g. older devices onboarded before MDM roll-out). Treat with caution before retiring — verify physically if possible.
          </div>
          <div style={{ background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 12, overflow: "hidden" }}>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    {[
                      { col: "serial",      label: "Serial" },
                      { col: "description", label: "Description" },
                      { col: "type",        label: "Type" },
                      { col: "status",      label: "Status" },
                      { col: "location",    label: "Location" },
                      { col: "assignedTo",  label: "Assigned (Jira)" },
                      { col: "dateOrdered", label: "Date Ordered" },
                    ].map(({ col, label }) => (
                      <TH key={col} col={col} label={label} sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {staleJiraDevices.map((a, i) => {
                    const mismatch  = getMismatch(a);
                    const flagColor = mismatch === "conflict" ? "var(--warn)" : mismatch === "one-sided" ? "var(--danger)" : null;
                    return (
                      <tr key={a.jiraKey || i} style={{ borderBottom: "1px solid var(--border)", background: i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.01)" }}>
                        <td style={{ padding: "8px 14px", fontSize: 12, fontFamily: "var(--font-mono)", fontWeight: 600, color: "var(--accent)", whiteSpace: "nowrap" }}>{a.serial || "—"}</td>
                        <td style={{ padding: "8px 14px", fontSize: 12, color: "var(--text)", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={a.description}>{a.description || "—"}</td>
                        <td style={{ padding: "8px 14px", fontSize: 11, color: "var(--muted)", whiteSpace: "nowrap" }}>{a.type || "—"}</td>
                        <td style={{ padding: "8px 14px", whiteSpace: "nowrap" }}><StatusChip status={a.status} /></td>
                        <td style={{ padding: "8px 14px", fontSize: 11, color: "var(--muted)", whiteSpace: "nowrap" }}>{a.location || "—"}</td>
                        <td style={{ padding: "8px 14px", fontSize: 12, color: flagColor || "var(--text)", whiteSpace: "nowrap" }}>{a.assignedTo || <span style={{ color: "var(--muted)" }}>—</span>}</td>
                        <td style={{ padding: "8px 14px", fontSize: 12, fontFamily: "var(--font-mono)", color: "var(--muted)", whiteSpace: "nowrap" }}>{a.dateOrdered || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {staleJiraDevices.length === 0 && done && (
                <div style={{ padding: 40, textAlign: "center", color: "var(--muted)", fontSize: 13 }}>No devices match the current filters.</div>
              )}
            </div>
          </div>
        </>
      )}

      {/* ── Stale (Azure) tab ── */}
      {loadedCount > 0 && activeTab === "stale-azure" && (
        <>
          <div style={{ background: "rgba(245,158,11,0.07)", border: "1px solid rgba(245,158,11,0.2)", borderRadius: 10, padding: "10px 16px", fontSize: 13, color: "var(--muted)", marginBottom: 16 }}>
            These <strong style={{ color: "var(--text)" }}>{stats.staleAzure} devices</strong> are enrolled in Intune but have <strong style={{ color: "var(--text)" }}>not been seen in 90+ days</strong>. A device last seen 2+ years ago is a stronger cleanup candidate than one seen last month. Sorted oldest-first by default.
          </div>
          <div style={{ background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 12, overflow: "hidden" }}>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    {[
                      { col: "serial",            label: "Serial" },
                      { col: "intuneHostname",    label: "Intune Hostname" },
                      { col: "description",       label: "Description" },
                      { col: "type",              label: "Type" },
                      { col: "status",            label: "Status" },
                      { col: "location",          label: "Location" },
                      { col: "assignedTo",        label: "Assigned (Jira)" },
                      { col: "defenderLastUser",  label: "Last Login (Azure)" },
                      { col: "defenderLastLogon", label: "Last Seen" },
                      { col: "dateOrdered",       label: "Date Ordered" },
                    ].map(({ col, label }) => (
                      <TH key={col} col={col} label={label} sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {staleAzureDevices.map((a, i) => {
                    const days     = daysSince(a.defenderLastLogon);
                    const { color: sColor } = stalenessLabel(days);
                    const mismatch  = getMismatch(a);
                    const flagColor = mismatch === "conflict" ? "var(--warn)" : mismatch === "one-sided" ? "var(--danger)" : null;
                    return (
                      <tr key={a.jiraKey || i} style={{ borderBottom: "1px solid var(--border)", background: i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.01)" }}>
                        <td style={{ padding: "8px 14px", fontSize: 12, fontFamily: "var(--font-mono)", fontWeight: 600, color: "var(--accent)", whiteSpace: "nowrap" }}>{a.serial || "—"}</td>
                        <td style={{ padding: "8px 14px", fontSize: 12, fontFamily: "var(--font-mono)", color: a.intuneHostname ? "var(--text)" : "var(--muted)", whiteSpace: "nowrap" }}>{a.intuneHostname || "—"}</td>
                        <td style={{ padding: "8px 14px", fontSize: 12, color: "var(--text)", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={a.description}>{a.description || "—"}</td>
                        <td style={{ padding: "8px 14px", fontSize: 11, color: "var(--muted)", whiteSpace: "nowrap" }}>{a.type || "—"}</td>
                        <td style={{ padding: "8px 14px", whiteSpace: "nowrap" }}><StatusChip status={a.status} /></td>
                        <td style={{ padding: "8px 14px", fontSize: 11, color: "var(--muted)", whiteSpace: "nowrap" }}>{a.location || "—"}</td>
                        <td style={{ padding: "8px 14px", fontSize: 12, color: flagColor || "var(--text)", whiteSpace: "nowrap" }}>{a.assignedTo || <span style={{ color: "var(--muted)" }}>—</span>}</td>
                        <td style={{ padding: "8px 14px", fontSize: 12, color: flagColor || "var(--muted)", whiteSpace: "nowrap" }}>{a.defenderLastUser || <span style={{ color: "var(--muted)" }}>—</span>}</td>
                        <td style={{ padding: "8px 14px", whiteSpace: "nowrap" }}>
                          {days !== null
                            ? <span style={{ color: sColor, fontSize: 12, fontWeight: 600 }}>{days}d ago</span>
                            : <span style={{ color: "var(--muted)", fontSize: 11 }}>—</span>}
                        </td>
                        <td style={{ padding: "8px 14px", fontSize: 12, fontFamily: "var(--font-mono)", color: "var(--muted)", whiteSpace: "nowrap" }}>{a.dateOrdered || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {staleAzureDevices.length === 0 && done && (
                <div style={{ padding: 40, textAlign: "center", color: "var(--muted)", fontSize: 13 }}>No stale Azure devices match the current filters.</div>
              )}
            </div>
          </div>
        </>
      )}

      {/* ── Battery Health tab ── */}
      {loadedCount > 0 && activeTab === "battery" && (
        <>
          <div style={{ background: "rgba(245,158,11,0.07)", border: "1px solid rgba(245,158,11,0.22)", borderRadius: 10, padding: "10px 16px", fontSize: 13, color: "var(--muted)", marginBottom: 16, display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
            <div>
              {batteryMode === "flagged" ? "Shows devices currently flagged by the Battery Safety Program for Layer 2 physical inspection. Jira ticket creation remains owned by the existing Azure Logic App." : "Shows the latest battery health record received in the last 30 days for each reporting device. Jira ticket details only appear for flagged devices."}
              {batteryLoadedAt && <span> Last loaded {formatDateTime(batteryLoadedAt)}.</span>}
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              {[{ id: "flagged", label: "Flagged" }, { id: "all", label: "All Recent" }].map(mode => (
                <button key={mode.id} onClick={() => handleBatteryModeChange(mode.id)} disabled={batteryLoading} style={{ background: batteryMode === mode.id ? "rgba(79,156,249,0.14)" : "var(--bg2)", border: `1px solid ${batteryMode === mode.id ? "var(--accent)" : "var(--border)"}`, color: batteryMode === mode.id ? "var(--accent)" : "var(--muted)", padding: "6px 12px", borderRadius: 8, cursor: batteryLoading ? "default" : "pointer", fontSize: 12, fontWeight: batteryMode === mode.id ? 700 : 500, opacity: batteryLoading ? 0.6 : 1 }}>
                  {mode.label}
                </button>
              ))}
              <button onClick={loadBatteryHealth} disabled={batteryLoading} style={{ background: "var(--bg2)", border: "1px solid var(--border)", color: "var(--muted)", padding: "6px 12px", borderRadius: 8, cursor: batteryLoading ? "default" : "pointer", fontSize: 12, opacity: batteryLoading ? 0.6 : 1 }}>
                {batteryLoading ? "Loading..." : "Reload Battery"}
              </button>
            </div>
          </div>

          <div style={{ background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 12, padding: "12px 14px", marginBottom: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", marginBottom: 10 }}>
              <div>
                <div style={{ color: "var(--text)", fontWeight: 800, fontSize: 14 }}>Battery workflow sanity check</div>
                <div style={{ color: "var(--muted)", fontSize: 11 }}>
                  Latest Azure Logic App runs
                  {batteryWorkflowLoadedAt && <span> · checked {formatDateTime(batteryWorkflowLoadedAt)}</span>}
                </div>
              </div>
              {batteryWorkflowLoading && (
                <span style={{ color: "var(--muted)", fontSize: 12, display: "inline-flex", gap: 8, alignItems: "center" }}>
                  <span className="spinner" style={{ width: 12, height: 12, flexShrink: 0 }} />
                  Checking…
                </span>
              )}
            </div>
            {batteryWorkflowError && (
              <div style={{ color: "var(--warn)", background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.22)", borderRadius: 8, padding: "8px 10px", fontSize: 12 }}>
                Could not load Logic App run history: {batteryWorkflowError}
              </div>
            )}
            {!batteryWorkflowError && !batteryWorkflowLoading && batteryWorkflowRuns.length === 0 && batteryWorkflowLoadedAt && (
              <div style={{ color: "var(--muted)", fontSize: 12 }}>No recent Logic App runs were returned.</div>
            )}
            {!batteryWorkflowError && batteryWorkflowRuns.length > 0 && (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 10 }}>
                {batteryWorkflowRuns.slice(0, 3).map(run => (
                  <div key={run.id || run.name} style={{ border: "1px solid var(--border)", background: "var(--bg3)", borderRadius: 10, padding: "10px 12px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
                      <WorkflowRunChip status={run.status} />
                      <span style={{ color: "var(--muted)", fontSize: 11 }}>{formatDuration(run.durationMs)}</span>
                    </div>
                    <div style={{ color: "var(--text)", fontSize: 12, fontWeight: 650, marginTop: 6 }}>{formatDateTime(run.startTime) || "No start time"}</div>
                    <div style={{ color: "var(--muted)", fontSize: 11, marginTop: 2 }}>
                      Ended {formatDateTime(run.endTime) || "not yet"}{run.triggerStatus ? ` · Trigger ${run.triggerStatus}` : ""}
                    </div>
                    {run.error && <div style={{ color: "var(--danger)", fontSize: 11, marginTop: 6, lineHeight: 1.35 }}>{String(run.error).slice(0, 180)}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>

          {batteryLoading && (
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16, color: "var(--muted)", fontSize: 13 }}>
              <span className="spinner" style={{ width: 14, height: 14, flexShrink: 0, borderTopColor: "var(--warn)" }} />
              {batteryMode === "flagged" ? "Loading flagged battery records from Log Analytics..." : "Loading latest fleet battery records from Log Analytics..."}
            </div>
          )}

          {batteryError && (
            <div style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 10, padding: "14px 18px", color: "var(--danger)", fontSize: 13, marginBottom: 20 }}>
              Failed to load battery health: {batteryError}
            </div>
          )}

          {!batteryLoading && !batteryError && batteryViewSummary && (
            <div style={{ display: "flex", gap: 10, marginBottom: 24, flexWrap: "wrap" }}>
              {[
                ...(batteryMode === "all" ? [{ label: "Reporting", value: batteryViewSummary.total || 0, color: "var(--success)", sub: "latest 30 days" }] : []),
                { label: "Flagged", value: batteryViewSummary.flagged || 0, color: "var(--danger)", sub: "inspection required" },
                ...(batteryMode === "all" ? [{ label: "Normal", value: batteryViewSummary.normal || 0, color: "var(--success)", sub: "not flagged" }] : []),
                { label: "Health <=80%", value: batteryViewSummary.under80 || 0, color: "var(--danger)", sub: "battery health" },
                { label: "Cycles >=500", value: batteryViewSummary.over500 || 0, color: "var(--warn)", sub: "cycle count" },
                { label: "Open Tickets", value: batteryViewSummary.openTickets || 0, color: "var(--success)", sub: "tracked in Jira" },
                { label: "No Ticket", value: batteryViewSummary.missingTickets || 0, color: "var(--danger)", sub: "needs follow-up" },
              ].map(s => (
                <div key={s.label} style={{ background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 10, padding: "12px 18px", minWidth: 120 }}>
                  <div style={{ fontSize: 24, fontWeight: 700, color: s.color, fontFamily: "'DM Sans', sans-serif", lineHeight: 1 }}>{s.value}</div>
                  <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 3, textTransform: "uppercase", letterSpacing: "0.07em" }}>{s.label}</div>
                  <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 1 }}>{s.sub}</div>
                </div>
              ))}
            </div>
          )}

          {!batteryLoading && !batteryError && batteryRows.length === 0 && batteryLoadedAt && (
            <div style={{ padding: 40, textAlign: "center", color: "var(--muted)", fontSize: 13, background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 12 }}>
              {batteryMode === "flagged" ? "No currently flagged battery records were returned." : "No recent battery health records were returned."}
            </div>
          )}

          {!batteryLoading && !batteryError && batteryRows.length > 0 && (
            <div style={{ background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 12, overflow: "hidden" }}>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      {[
                        { col: "serial", label: "Serial" },
                        { col: "deviceName", label: "Device" },
                        { col: "campus", label: "Campus" },
                        { col: "assignedTo", label: "Jira Assigned To" },
                        { col: "eolYear", label: "EOL" },
                        { col: "defenderLastUser", label: "Last Login (Azure)" },
                        { col: "defenderLastLogon", label: "Last Seen" },
                        { col: "batteryHealthPercent", label: "Health" },
                        { col: "cycleCount", label: "Cycles" },
                        { col: "notes", label: "Reason" },
                        { col: "captureDate", label: "Captured" },
                        { col: "ticket", label: "Ticket" },
                        { col: "ticketAssignee", label: "Ticket Assigned To" },
                        { col: "ticketStatus", label: "Ticket Status" },
                      ].map(({ col, label }) => (
                        <TH key={col} col={col} label={label} sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {batteryRows.map((a, i) => (
                      <tr key={a.serial || i} style={{ borderBottom: "1px solid var(--border)", background: (a.flagged && !a.ticket?.key) ? "rgba(239,68,68,0.05)" : i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.01)" }}>
                        <td style={{ padding: "8px 14px", fontSize: 12, fontFamily: "var(--font-mono)", fontWeight: 600, color: "var(--accent)", whiteSpace: "nowrap" }}>{a.serial || "—"}</td>
                        <td style={{ padding: "8px 14px", fontSize: 12, color: "var(--text)", whiteSpace: "nowrap" }}>{a.deviceName || <span style={{ color: "var(--muted)" }}>—</span>}</td>
                        <td style={{ padding: "8px 14px", fontSize: 11, color: "var(--muted)", whiteSpace: "nowrap" }}>{a.campus || "—"}</td>
                        <td style={{ padding: "8px 14px", fontSize: 12, color: "var(--text)", whiteSpace: "nowrap" }}>{a.assignedTo || <span style={{ color: "var(--muted)" }}>—</span>}</td>
                        <td style={{ padding: "8px 14px", fontSize: 12, fontFamily: "var(--font-mono)", fontWeight: 700, color: a.eolYear ? "var(--text)" : "var(--muted)", whiteSpace: "nowrap" }}>{a.eolYear || "—"}</td>
                        <td style={{ padding: "8px 14px", fontSize: 12, color: "var(--muted)", whiteSpace: "nowrap" }}>{a.defenderLastUser || <span style={{ color: "var(--muted)" }}>—</span>}</td>
                        <td style={{ padding: "8px 14px", whiteSpace: "nowrap" }}><LastSeenBadge days={daysSince(a.defenderLastLogon)} /></td>
                        <td style={{ padding: "8px 14px", whiteSpace: "nowrap" }}><HealthChip value={a.batteryHealthPercent} /></td>
                        <td style={{ padding: "8px 14px", fontSize: 12, fontFamily: "var(--font-mono)", color: Number(a.cycleCount) >= 500 ? "var(--warn)" : "var(--muted)", whiteSpace: "nowrap" }}>{a.cycleCount ?? "—"}</td>
                        <td style={{ padding: "8px 14px", fontSize: 12, color: "var(--text)", maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={a.notes || a.layer1Status}>{a.notes || a.layer1Status || "—"}</td>
                        <td style={{ padding: "8px 14px", fontSize: 11, color: "var(--muted)", whiteSpace: "nowrap" }}>{formatDateTime(a.captureDate) || "—"}</td>
                        <td style={{ padding: "8px 14px", whiteSpace: "nowrap" }}>{a.flagged ? <TicketChip ticket={a.ticket} /> : <span style={{ color: "var(--muted)", fontSize: 11 }}>Not flagged</span>}</td>
                        <td style={{ padding: "8px 14px", fontSize: 12, color: a.ticket?.assignee ? "var(--text)" : "var(--muted)", whiteSpace: "nowrap" }}>{a.flagged ? (a.ticket?.assignee || a.ticket?.assigneeEmail || "Unassigned") : "-"}</td>
                        <td style={{ padding: "8px 14px", fontSize: 11, color: a.ticket?.status ? "var(--text)" : "var(--muted)", whiteSpace: "nowrap" }}>{a.flagged ? (a.ticket?.status || a.ticketLookupError || "-") : "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
